import React, { FC, useCallback, useMemo, useState } from 'react';

import { VisuallyHidden } from '@rbilabs/components-library/build/components/visually-hidden';
import { useIntl } from 'react-intl';

import { IOffer, ISanityVendorConfigs } from '@rbi-ctg/menu';
import ConfirmDialog from 'components/confirm-dialog';
import Modal from 'components/modal';
import { OfferDisclaimer } from 'components/offers/ui-refresh/offer-disclaimer';
import Picture from 'components/picture';
import { useAndroidBackButton } from 'hooks/use-android-back-button';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { LoyaltyOffer } from 'state/loyalty/types';
import noop from 'utils/noop';

import QRCode from '../qrcode';

import { OBJECT_FIT_CONTAIN } from './constants';
import {
  BodyContent,
  CouponTitle,
  Description,
  DescriptionWrapper,
  Directions,
  ModalInner,
  Name,
  PictureHeading,
  QRCodeContainer,
  RedeemInNext,
  ShortCode,
  ShortCodeWrapper,
  StyledCountdown,
  UseCodeAtCashier,
} from './styled';
import { IRedemptionModalProps } from './types';

/**
 * Get rPOS PLU for QR code usage.
 * @param vendorConfig vendor configuration object.
 * @returns string representing rPOS PLU if present; empty string otherwise.
 */
function getPlu(vendorConfig: ISanityVendorConfigs): string {
  const pluType = vendorConfig?.rpos?.pluType;
  if (!pluType) {
    return '';
  }
  return vendorConfig?.rpos?.[pluType] || '';
}

/**
 * Hook to retrieve barcode contents based on various LD flags.
 * @param selectedOffer offer for which barcode contents should be calculated.
 * @returns string to be used as barcode contents.
 */
export function useBarcodeContents(selectedOffer: IOffer | LoyaltyOffer): string {
  const { user } = useAuthContext();

  const cognitoId = useMemo(() => user?.cognitoId || 0, [user]);

  const enableCognitoIDQrCode = useFlag(LaunchDarklyFlag.ENABLE_COGNITOID_QR_CODE);
  const enablePluOfferQrCode = useFlag(LaunchDarklyFlag.ENABLE_PLU_OFFER_QR_CODE);

  const shortCode = selectedOffer?.shortCode || '';
  const plu = getPlu(selectedOffer.vendorConfigs as ISanityVendorConfigs);

  if (enableCognitoIDQrCode) {
    // If no PLU is available, fall back to using the short code.
    const code = plu || shortCode;
    return `BKCP01_${cognitoId}_${code}`;
  }

  if (enablePluOfferQrCode) {
    return plu;
  }

  return shortCode;
}

const RedemptionModal: FC<IRedemptionModalProps> = ({
  redemptionStartTime = 0,
  selectedOffer,
  onDismiss = noop,
}) => {
  const [closeConfirmationIsOpen, setCloseConfirmationIsOpen] = useState(false);
  const handleModalDismiss = useCallback(() => {
    setCloseConfirmationIsOpen(true);
  }, []);
  const handleDialogDismiss = useCallback(() => {
    setCloseConfirmationIsOpen(false);
  }, []);

  const { formatMessage } = useIntl();
  const barcodeOptions = {
    margin: 1,
    scale: 6,
    color: {
      dark: '#502314',
      light: '#FFFFFF00',
    },
  };
  window.history.pushState(null, '', document.URL);
  window.addEventListener('popstate', () => {
    handleModalDismiss();
  });
  const enableOfferQrCode = useFlag(LaunchDarklyFlag.ENABLE_OFFER_QR_CODE);
  const barcodeContents = useBarcodeContents(selectedOffer);
  useAndroidBackButton(handleModalDismiss);
  return (
    <Modal
      onDismiss={handleModalDismiss}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Redeem Offer',
      }}
    >
      <ModalInner data-testid="redemption-modal">
        <BodyContent>
          <PictureHeading>
            <Picture
              image={selectedOffer?.localizedImage?.locale?.app!}
              alt={selectedOffer?.localizedImage?.locale?.imageDescription! || ''}
              placeholderAspectRatio={400 / 170}
              objectFitContain={OBJECT_FIT_CONTAIN}
            />
          </PictureHeading>
          <CouponTitle data-testid="redemption-title">
            <Name content={selectedOffer?.name?.localeRaw} />
          </CouponTitle>
          <DescriptionWrapper data-testid="offer-description">
            <Description content={selectedOffer?.description?.localeRaw} />
          </DescriptionWrapper>
          <UseCodeAtCashier>{formatMessage({ id: 'useCodeAtCashier' })}</UseCodeAtCashier>
          <ShortCodeWrapper>
            {enableOfferQrCode && (
              <QRCodeContainer>
                <QRCode
                  data-testid="redemption-qr-code"
                  barcode={barcodeContents}
                  options={barcodeOptions}
                />
              </QRCodeContainer>
            )}
            <ShortCode
              qrcode={enableOfferQrCode}
              data-testid="redemption-short-code"
              aria-describedby="redemption-code-description"
            >
              {selectedOffer.shortCode || ''}
            </ShortCode>
            <VisuallyHidden id="redemption-code-description">
              {formatMessage({ id: 'redemptionCode' })}
            </VisuallyHidden>
          </ShortCodeWrapper>
          <Directions>
            <RedeemInNext>{formatMessage({ id: 'redeemInNext' })}</RedeemInNext>
            <StyledCountdown
              useClockStyle
              endTime={redemptionStartTime + 60 * 15 * 1000}
              onFinish={onDismiss}
            />
          </Directions>
          <OfferDisclaimer offer={selectedOffer} />
        </BodyContent>
      </ModalInner>
      {closeConfirmationIsOpen && (
        <ConfirmDialog
          heading={formatMessage({ id: 'offerConfirmDialogHeading' })}
          body={formatMessage({ id: 'offerConfirmDialogBody' })}
          cancelLabel={formatMessage({ id: 'cancel' })}
          confirmLabel={formatMessage({ id: 'offerConfirmDialogConfirmLabel' })}
          onDismiss={handleDialogDismiss}
          onCancel={handleDialogDismiss}
          onConfirm={onDismiss}
          modalAppearanceEventMessage="Confirm Close Redemption"
        />
      )}
    </Modal>
  );
};

export default RedemptionModal;
